import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db, ref, onValue, get } from "./firebase";
import { useAuth } from "./AuthContext";
import { FaBook, FaBell } from "react-icons/fa";
import { formatCourseId } from "./utils/formatCourse";
import { HOLIDAY_PRESETS, FIREBASE_HOLIDAY_PATH } from "./constants/holidayPresets";
import { X } from "lucide-react";
import { DashboardLoading, NoCourse } from "./DashboardStates";

// Confetti canvas for holiday banner
function Confetti({ colors, active }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const startRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    if (!active || !colors?.length) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const w = (canvas.width = window.innerWidth);
    const h = (canvas.height = window.innerHeight);
    const count = 55;

    const create = () => {
      const res = [];
      for (let i = 0; i < count; i++) {
        res.push({
          x: Math.random() * w,
          y: -20,
          r: 4 + Math.random() * 8,
          color: colors[Math.floor(Math.random() * colors.length)],
          vy: 2 + Math.random() * 4,
          vx: (Math.random() - 0.5) * 2,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 10,
        });
      }
      return res;
    };

    particlesRef.current = create();
    startRef.current = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      if (elapsed > 5) return;
      ctx.clearRect(0, 0, w, h);
      particlesRef.current.forEach((p) => {
        p.y += p.vy;
        p.x += p.vx;
        p.vy += 0.05;
        p.rotation += p.rotationSpeed;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r * 1.5);
        ctx.restore();
      });
      frameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [active, colors]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-30"
      aria-hidden
    />
  );
}

// Date formatting helper
const formatDate = (date, options = {}) => {
  if (!date) return '';
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

const Dashboard = () => {
  const [username, setUsername] = useState("Loading...");
  const { user } = useAuth();
  const [courseInfo, setCourseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [courseProgress, setCourseProgress] = useState(0);

  // State for dynamic data
  const [upcomingAssignments, setUpcomingAssignments] = useState([]);
  const [latestChapterDone, setLatestChapterDone] = useState(null);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [latestAnnouncement, setLatestAnnouncement] = useState(null);
  const [holidayGreeting, setHolidayGreeting] = useState(null);
  const [holidayBannerDismissed, setHolidayBannerDismissed] = useState(false);

  const contactNumber = "+201004672283"; // WhatsApp contact number
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      try {
        setLoading(true);
        const email = user.email;
        let foundUser = null;
        let courseName = null;
        let unsubscribeFunctions = [];

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
          if (foundUser) {
            courseName = foundUser.enrolledCourse;
          }
        }

        // If not found in users collection, search in courses/students
        if (!foundUser) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  courseName = courseId;
                  break;
                }
              }
            }
          }
        }

        if (foundUser) {
          setUsername(foundUser.name);
          setEnrolledCourse(courseName);
        }

        // If we found a course, fetch its data
        if (courseName) {
          const courseRef = ref(db, `courses/${courseName}`);
          const unsubscribeCourse = onValue(courseRef, (snapshot) => {
            if (snapshot.exists()) {
              setCourseInfo(snapshot.val());
            }
          });
          unsubscribeFunctions.push(unsubscribeCourse);

          // Fetch chapters progress
          const chaptersRef = ref(db, `courses/${courseName}/chapters`);
          const unsubscribeChapters = onValue(chaptersRef, (snapshot) => {
            if (snapshot.exists()) {
              const chaptersData = snapshot.val();
              const completedChapters = Object.values(chaptersData).filter((ch) => ch.done).length;
              const totalChapters = Object.keys(chaptersData).length;
              const calculatedProgress = Math.round((completedChapters / totalChapters) * 100);
              setCourseProgress(calculatedProgress);

              // Find the latest completed chapter
              const completedChaptersList = Object.values(chaptersData)
                .filter((ch) => ch.done)
                .map(ch => ({
                  ...ch,
                  completedAt: ch.completedAt ? new Date(ch.completedAt) : new Date(0)
                }))
                .sort((a, b) => b.completedAt - a.completedAt);

              setLatestChapterDone(completedChaptersList[0] || null);
            }
          });
          unsubscribeFunctions.push(unsubscribeChapters);

          // Fetch assignments
          const assignmentsRef = ref(db, `courses/${courseName}/homeworks`);
          const unsubscribeAssignments = onValue(assignmentsRef, (snapshot) => {
            if (snapshot.exists()) {
              const assignmentsData = Object.entries(snapshot.val()).map(([id, hw]) => ({
                id,
                ...hw,
                dueDateObj: new Date(hw.dueDate)
              }));

              const today = new Date();
              const upcoming = assignmentsData
                .filter(assignment => assignment.dueDateObj > today)
                .sort((a, b) => a.dueDateObj - b.dueDateObj)
                .slice(0, 3);

              setUpcomingAssignments(upcoming);
            }
          });
          unsubscribeFunctions.push(unsubscribeAssignments);

          // Fetch meetings
          const meetingsRef = ref(db, `courses/${courseName}/meetings`);
          const unsubscribeMeetings = onValue(meetingsRef, (snapshot) => {
            if (snapshot.exists()) {
              const meetingsData = Object.entries(snapshot.val()).map(([id, meeting]) => ({
                id,
                ...meeting,
                dateObj: new Date(meeting.date)
              }));

              const today = new Date();
              const upcoming = meetingsData
                .filter(meeting => meeting.dateObj > today)
                .sort((a, b) => a.dateObj - b.dateObj)
                .slice(0, 2);

              setUpcomingMeetings(upcoming);
            }
          });
          unsubscribeFunctions.push(unsubscribeMeetings);
        }

        // Fetch latest announcement
        if (courseName) {
          const announcementsRef = ref(db, `courses/${courseName}/announcements`);
          const unsubscribeAnnouncements = onValue(announcementsRef, (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.val();
              const announcementsList = Object.entries(data).map(([id, announcement]) => ({
                id,
                ...announcement,
                date: new Date(announcement.date)
              })).sort((a, b) => b.date - a.date);
              setLatestAnnouncement(announcementsList[0] || null);
            } else {
              setLatestAnnouncement(null);
            }
          });
          unsubscribeFunctions.push(unsubscribeAnnouncements);
        }

        // Holiday greeting banner (global setting)
        const holidayRef = ref(db, FIREBASE_HOLIDAY_PATH);
        const unsubscribeHoliday = onValue(holidayRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            if (data.enabled && data.holidayId) setHolidayGreeting(data);
            else setHolidayGreeting(null);
          } else setHolidayGreeting(null);
        });
        unsubscribeFunctions.push(unsubscribeHoliday);

        return () => {
          // Call all unsubscribe functions
          unsubscribeFunctions.forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
              unsubscribe();
            }
          });
        };
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  if (loading) return <DashboardLoading />;

  if (!enrolledCourse) {
    return <NoCourse description="Once you're enrolled, your progress, homework, and announcements will show up here." />;
  }

  // Check if user has pending registration
  if (enrolledCourse === "pending_registration") {
    // Redirect to complete registration page
    navigate("/complete-registration", { replace: true });
    return null;
  }

  const holidayPreset = holidayGreeting ? HOLIDAY_PRESETS.find((p) => p.id === holidayGreeting.holidayId) : null;
  const showHolidayBanner = holidayPreset && !holidayBannerDismissed;
  const holidayImageUrl = holidayPreset?.image
    ? `${process.env.PUBLIC_URL || ""}${holidayPreset.image}`
    : null;

  return (
    <div className="mx-auto px-4 py-6 w-full relative">
      {/* Confetti when holiday banner is visible */}
      <Confetti colors={holidayPreset?.confettiColors} active={showHolidayBanner} />

      {/* Holiday greeting banner */}
      {showHolidayBanner && (
        <div
          className="relative z-40 mb-6 rounded-xl border border-white/20 overflow-hidden shadow-lg"
          style={{
            background: `linear-gradient(135deg, ${holidayPreset.confettiColors[0]}22 0%, ${holidayPreset.confettiColors[1] ?? holidayPreset.confettiColors[0]}33 50%, ${holidayPreset.confettiColors[2] ?? holidayPreset.confettiColors[0]}22 100%)`,
            borderColor: `${holidayPreset.confettiColors[0]}44`,
          }}
        >
          <div className="flex items-center justify-between gap-4 px-5 py-4">
            <div className="flex items-center gap-4 min-w-0">
              {holidayImageUrl ? (
                <img
                  src={holidayImageUrl}
                  alt=""
                  className="h-16 w-16 object-contain shrink-0 rounded-lg bg-white/10"
                  onError={(e) => {
                    e.target.style.display = "none";
                    const fallback = e.target.nextElementSibling;
                    if (fallback) fallback.style.display = "block";
                  }}
                />
              ) : null}
              <span
                className="text-4xl shrink-0"
                style={{ display: holidayImageUrl ? "none" : "block" }}
                role="img"
                aria-hidden
              >
                {holidayPreset.fallbackEmoji}
              </span>
              <h2 className="text-xl font-bold text-white truncate">{holidayPreset.label}</h2>
            </div>
            <button
              type="button"
              onClick={() => setHolidayBannerDismissed(true)}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors shrink-0"
              aria-label="Dismiss banner"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="mb-6">
        <div className="bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                Welcome back, {username}!
              </h1>
              <p className="text-blue-100 text-base md:text-lg mt-1">
                {enrolledCourse ? `Course: ${formatCourseId(enrolledCourse)}` : "No course enrolled"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate('/dashboard/videos')}
                className="flex-1 md:flex-none px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 text-sm md:text-base"
              >
                <FaBook className="text-lg" />
                <span>Continue Learning</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/announcements')}
                className="flex-1 md:flex-none px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 text-sm md:text-base"
              >
                <FaBell className="text-lg" />
                <span>Announcements</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Syllabus Progress */}
        <div className="dash-card bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Syllabus Progress</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm text-gray-400">
              <span>Overall Progress</span>
              <span>{courseProgress}%</span>
            </div>
            <div className="dash-track w-full h-3 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-sky-500 rounded-full transition-all duration-500"
                style={{ width: `${courseProgress}%` }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="dash-inset bg-gray-700/30 rounded-lg p-3">
                <div className="text-sm text-gray-400 mb-1">Completed</div>
                <div className="text-xl font-semibold text-white">
                  {courseInfo?.chapters ? Object.values(courseInfo.chapters).filter(ch => ch.done).length : 0}
                </div>
              </div>
              <div className="dash-inset bg-gray-700/30 rounded-lg p-3">
                <div className="text-sm text-gray-400 mb-1">Total</div>
                <div className="text-xl font-semibold text-white">
                  {courseInfo?.chapters ? Object.keys(courseInfo.chapters).length : 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Latest Announcement */}
        <div className="dash-card bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Latest Announcement</h2>
          {latestAnnouncement ? (
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-white">{latestAnnouncement.title}</h3>
              <p className="text-gray-400 text-sm">{latestAnnouncement.content}</p>
              <div className="text-xs text-gray-500">
                {formatDate(latestAnnouncement.date)}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400">No announcements yet. Course news will show up here.</p>
          )}
        </div>
      </div>

      {/* Upcoming Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Assignments */}
        <div className="dash-card bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Upcoming Assignments</h2>
          {upcomingAssignments.length > 0 ? (
            <div className="space-y-4">
              {upcomingAssignments.map((assignment) => (
                <div key={assignment.id} className="p-4 dash-inset bg-gray-700/30 rounded-lg">
                  <h3 className="font-medium text-white">{assignment.title}</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Due: {formatDate(assignment.dueDateObj, { year: undefined })}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No upcoming assignments. New homework will show up here.</p>
          )}
        </div>

        {/* Latest Chapter Completed */}
        <div className="dash-card bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Latest Chapter Completed</h2>
          {latestChapterDone ? (
            <div className="p-4 dash-inset bg-gray-700/30 rounded-lg">
              <h3 className="font-medium text-white">{latestChapterDone.title || "Untitled Chapter"}</h3>
              <p className="text-sm text-gray-400 mt-1">Completed on: {formatDate(latestChapterDone.completedAt)}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-400">No chapters completed yet. Finished chapters will show up here.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;